// Notificações Push (Web Push API) — subscrição/permissão do browser.
//
// Não pede permissão sozinho em nenhum momento: só é invocado a partir de
// uma ação explícita do utilizador em Configurações > Notificações
// (NotificationsSection), nunca automaticamente ao abrir a app — o iOS
// Safari em particular penaliza pedidos de permissão não solicitados, e
// mesmo no Chrome isso tende a ser ignorado/bloqueado pelo utilizador.
import { ensureSubscriptionDeviceId, resolveRuntimePlatform } from "./erpContext";
import { apiGetPushConfig, apiSubscribePush, apiUnsubscribePush } from "./authApi";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// iOS Safari só recebe Web Push quando a app foi "Adicionada ao Ecrã
// Principal" — nunca numa aba normal do Safari. Sem esta checagem, o
// pedido de permissão falha ou fica preso em silêncio, sem indicar ao
// utilizador porquê.
export function isIosNonStandalone() {
  if (typeof window === "undefined") return false;

  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  if (!isIos) return false;

  const isStandalone =
    window.navigator?.standalone === true ||
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)")?.matches === true);

  return !isStandalone;
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/service-worker.js");
  } catch (error) {
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Pede permissão de notificação (se ainda não concedida), subscreve o
 * PushManager do Service Worker, e envia a subscrição ao backend.
 * Lança erro com mensagem amigável em vez de deixar rejeições genéricas
 * do browser passarem por diante.
 */
export async function requestPermissionAndSubscribe(token) {
  if (!isPushSupported()) {
    throw new Error("Este dispositivo/navegador não suporta notificações push.");
  }

  if (isIosNonStandalone()) {
    throw new Error("No iPhone/iPad, adicione esta app ao ecrã principal primeiro (Partilhar → Adicionar ao Ecrã Principal) para poder receber notificações.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permissão de notificações não concedida.");
  }

  const registration = await navigator.serviceWorker.register("/service-worker.js");
  await navigator.serviceWorker.ready;

  const { vapidPublicKey, pushEnabled } = await apiGetPushConfig();
  if (!pushEnabled || !vapidPublicKey) {
    throw new Error("Notificações push não estão configuradas no servidor.");
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const deviceId = ensureSubscriptionDeviceId();
  const result = await apiSubscribePush(token, {
    deviceId,
    userAgent: window.navigator?.userAgent || "",
    platform: resolveRuntimePlatform(),
    subscription: subscription.toJSON(),
  });

  return result;
}

export async function unsubscribePush(token) {
  const deviceId = ensureSubscriptionDeviceId();

  if (isPushSupported()) {
    try {
      const registration = await navigator.serviceWorker.getRegistration("/service-worker.js");
      const subscription = await registration?.pushManager?.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (error) {
      // Segue para desativar no backend de qualquer forma.
    }
  }

  return apiUnsubscribePush(token, deviceId);
}
